import { ChevronRight } from "lucide-react";
import { PILL, STATE_ICON } from "@/components/registry/PublishSteps";
import { Elapsed } from "@/components/registry/LiveTime";
import { fmtMs } from "@/lib/publishSteps";
import type { AttemptSummary } from "@/lib/attemptSummary";
import type { StepState } from "@/lib/stepChecklist";
import { cn } from "@/lib/utils";

const SEGMENT: Record<StepState, string> = {
  pending: "bg-muted-foreground/20",
  running: "bg-blue-500 animate-pulse",
  ok: "bg-green-600",
  degraded: "bg-yellow-500",
  failed: "bg-destructive",
  skipped: "bg-muted-foreground/45",
};

/** The compact body of an attempt card: progress (n/total steps and a thin
 *  segmented bar, one segment per step), the step running now with its
 *  elapsed time, and a chip per independently published benchmark component
 *  (fixtures, glibc, musl, mac). The whole thing is ONE button that opens the
 *  detail drawer (only phrasing elements inside: it is a <button>); the data
 *  is lib/attemptSummary.ts. */
export function AttemptSummaryCard({
  summary,
  onOpen,
  label,
  expanded,
}: {
  summary: AttemptSummary;
  onOpen: () => void;
  /** Names the opener for assistive tech: "package@version". */
  label: string;
  expanded: boolean;
}) {
  const { done, total, segments, current, components, problems } = summary;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-haspopup="dialog"
      aria-expanded={expanded}
      aria-label={`Show step details of ${label}: ${done} of ${total} steps settled`}
      data-testid="attempt-summary"
      className="block w-full space-y-2 rounded-md px-1 py-1 text-left hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className="flex items-center justify-between gap-2 text-xs">
        <span className="font-medium tabular-nums" data-testid="summary-count">
          {done}/{total} steps
        </span>
        <span className="flex items-center gap-1.5 text-muted-foreground">
          {problems > 0 && (
            <span
              className={cn(
                "rounded-full border px-2 py-0.5 text-[11px] font-semibold",
                PILL.degraded,
              )}
            >
              {problems} to check
            </span>
          )}
          <span className="inline-flex items-center gap-0.5">
            Details
            <ChevronRight className="h-3.5 w-3.5" aria-hidden />
          </span>
        </span>
      </span>

      <span
        className="flex h-1.5 gap-px overflow-hidden rounded-full"
        aria-hidden
        data-testid="summary-bar"
      >
        {segments.map((s, i) => (
          <span key={i} className={cn("h-full flex-1", SEGMENT[s])} />
        ))}
      </span>

      <span
        className="flex min-w-0 flex-wrap items-baseline gap-x-2 text-xs"
        data-testid="summary-current"
      >
        {current ? (
          <>
            <span className="font-mono tabular-nums text-muted-foreground">
              {current.n}
            </span>
            <span className="min-w-0 truncate font-medium">
              {current.title}
            </span>
            {current.startedAt !== null ? (
              <Elapsed
                startedAt={current.startedAt}
                className="tabular-nums text-muted-foreground"
              />
            ) : (
              current.elapsedMs !== null && (
                <span className="tabular-nums text-muted-foreground">
                  {fmtMs(current.elapsedMs)}
                </span>
              )
            )}
            {current.alsoRunning > 0 && (
              <span className="text-muted-foreground">
                +{current.alsoRunning} more running
              </span>
            )}
            {current.progress && (
              <span className="block basis-full truncate text-muted-foreground">
                {current.progress}
              </span>
            )}
          </>
        ) : (
          <span className="text-muted-foreground">
            {done === total && total > 0
              ? "all steps settled"
              : "no step running"}
          </span>
        )}
      </span>

      <span
        className="flex flex-wrap gap-1.5"
        role="group"
        aria-label="Benchmark components"
        data-testid="summary-components"
      >
        {components.map((c) => {
          const Icon = STATE_ICON[c.state];
          return (
            <span
              key={c.key}
              title={c.reason ?? undefined}
              data-testid={`chip-${c.label}`}
              className={cn(
                "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold",
                PILL[c.state],
              )}
            >
              <Icon
                className={cn(
                  "h-3 w-3",
                  c.state === "running" && "animate-spin",
                )}
                aria-hidden
              />
              {c.label}
              <span className="font-normal">{c.stateLabel}</span>
            </span>
          );
        })}
      </span>
    </button>
  );
}
